<template>
    <div class="file-wrapper-applicant" :style="`width: ${applicantWidth};`">
        <div class="file-wrapper-applicant__header">
            <div class="file-wrapper-applicant__header__type">
                <PtIcon class="file-wrapper-applicant__header__type__icon" icon="SolidAssigneeCompany" />
                <span class="file-wrapper-applicant__header__type__name">{{ $t('fileWrapperMultiView.applicant') }}</span>
            </div>
            <div class="file-wrapper-applicant__header__title">
                {{ $t('fileWrapperMultiView.applicantArgument') }}
            </div>
        </div>
        <div v-if="nodata" class="file-wrapper-applicant__nodata">
            <PtNodata :nodataTips="$t('fileWrapperMultiView.responseNodata')" />
        </div>
        <div v-else class="file-wrapper-applicant__main">
            <div v-if="hasModify && showModify" class="file-wrapper-applicant__main__item file-wrapper-applicant__main__modify">
                <div class="file-wrapper-applicant__main__bar">
                    <span>{{ $t('fileWrapperMultiView.amendments') }}</span>
                    <button
                        v-if="showDoubleColView"
                        class="pt-ui-btn"
                        data-type="transparent"
                        data-size="medium"
                        @click="changeView('del', 'modify')"
                    >
                        <PtIcon
                            icon="SolidCloseBig"
                        />
                    </button>
                    <Popover
                        v-else
                        trigger="hover"
                        placement="top"
                        :disabled="hasArgument"
                        :visibleArrow="false"
                        class="file-wrapper-detail__popover"
                    >
                        <button
                            slot="reference"
                            :class="{
                                'pt-ui-btn': true,
                                'pt-ui-btn--diasbled': !hasArgument
                            }"
                            data-type="transparent"
                            with-icon
                            @click="changeView('add', 'argument')"
                        >
                            <PtIcon
                                icon="SolidAddLarge"
                            />
                            <span>{{ $t('fileWrapperMultiView.viewArgument') }}</span>
                        </button>
                        <span>{{ $t('fileWrapperMultiView.argumentDisabled') }}</span>
                    </Popover>
                </div>
                <FileWrapperMultiViewModify
                    class="file-wrapper-applicant__main__content"
                    :list="modifyFileData"
                    :round="currentRoundDetail.round"
                    :mrdt="currentMrdtInSingleView"
                    @onChange="changeCurrentMrdt"
                />
            </div>
            <div v-if="hasArgument && showArgument" class="file-wrapper-applicant__main__item file-wrapper-applicant__main__argument">
                <div class="file-wrapper-applicant__main__bar">
                    <span>{{ $t('fileWrapperMultiView.argument') }}</span>
                    <button
                        v-if="showDoubleColView"
                        class="pt-ui-btn"
                        data-type="transparent"
                        data-size="medium"
                        @click="changeView('del', 'argument')"
                    >
                        <PtIcon
                            icon="SolidCloseBig"
                        />
                    </button>
                    <Popover
                        v-else
                        trigger="hover"
                        placement="top"
                        :disabled="hasModify"
                        :visibleArrow="false"
                        class="file-wrapper-detail__popover"
                    >
                        <button
                            slot="reference"
                            :class="{
                                'pt-ui-btn': true,
                                'pt-ui-btn--diasbled': !hasModify
                            }"
                            data-type="transparent"
                            with-icon
                            @click="changeView('add', 'modify')"
                        >
                            <PtIcon
                                icon="SolidAddLarge"
                            />
                            <span>{{ $t('fileWrapperMultiView.viewAmendments') }}</span>
                        </button>
                        <span>{{ $t('fileWrapperMultiView.amendmentsDisabled') }}</span>
                    </Popover>
                </div>
                <FileWrapperMultiViewArgument
                    class="file-wrapper-applicant__main__content"
                    :list="argumentsData"
                    :mrdt="currentMrdtInSingleView"
                    @onChange="changeCurrentMrdt"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import PtNodata from '../PtNodata';
import FileWrapperMultiViewModify from './FileWrapperMultiViewModify.vue';
import FileWrapperMultiViewArgument from './FileWrapperMultiViewArgument.vue';
import useFileWrapperMultiView from '../../composables/use-file-wrapper-multi-view';
import { Popover } from 'element-ui';
import _ from 'lodash';
import { Icon as PtIcon } from 'patsnap-biz';
import '@patsnap-ui/icon/assets/solid/AssigneeCompany.svg';
import '@patsnap-ui/icon/assets/solid/AddLarge.svg';
import '@patsnap-ui/icon/assets/solid/CloseBig.svg';

const { currentRoundDetail } = useFileWrapperMultiView();

const responseData = computed(() => {
    return _.get(currentRoundDetail.value, 'response', []);
});

const modifyFileData = computed(() => {
    // 拼接list
    const list = [];
    _.forEach(responseData.value, responseItem => {
        const data = _.pick(responseItem, ['claims', 'desc']);
        for (const key in data) {
            const items = data[key];
            _.forEach(items, item => {
                if (key === 'desc' && !item.pdf) {
                    return;
                }
                list.push({
                    ...item,
                    type: key,
                });
            });
        }
    });
    return list;
});

const hasModify = computed(() => {
    return modifyFileData.value.length > 0;
});

const argumentsData = computed(() => {
    let list = [];
    _.forEach(responseData.value, responseItem => {
        const data = _.get(responseItem, 'arguments', []);
        list = _.concat(list, data);
    });
    return list;
});

const hasArgument = computed(() => {
    return argumentsData.value.length > 0;
});

const nodata = computed(() => {
    return !hasModify.value && !hasArgument.value;
});


// 刚进来的初始逻辑
// 如果modify第一条没数据，则展示argument
const firstModify = _.head(modifyFileData.value);
const hasFirstModifyValue = !!(_.get(firstModify, 'pdf') || _.get(firstModify, 'text'));
const showModify = ref(hasModify.value && hasFirstModifyValue);
const showArgument = ref(hasArgument.value && (!hasModify.value || !hasFirstModifyValue));

const showDoubleColView = computed(() => {
    return showArgument.value === true && showModify.value === true;
});

const changeView = (type, view) => {
    window.rvTrack('Change Patent File Wrapper Multi View Rejection');
    if (type === 'add') {
        if (view === 'argument' && hasArgument.value) showArgument.value = true;
        if (view === 'modify' && hasModify.value) showModify.value = true;
    } else if (type === 'del') {
        if (view === 'argument') showArgument.value = false;
        if (view === 'modify') showModify.value = false;
    }
};

const applicantWidth = computed(() => {
    if (nodata.value) {
        return 'calc(100% / 3)';
    }
    if (showDoubleColView.value) {
        return 'calc(100% / 3 * 2)';
    }
    return '50%';
});

// 用作两列初始化联动
const currentMrdtInSingleView = ref(null);

const changeCurrentMrdt = (mrdt) => {
    if (!showDoubleColView.value) {
        currentMrdtInSingleView.value = mrdt;
    }
};

</script>


<style lang="scss">
.file-wrapper-applicant {
    height: 100%;
    //width: 100%;
    padding: 0 16px 16px 16px;
    //overflow-y: auto;
    display: flex;
    flex-direction: column;

    .file-wrapper-applicant__header {
        height: 40px;
        display: flex;
        align-items: center;
        flex-shrink: 0;

        .file-wrapper-applicant__header__type {
            border-radius: 20px;
            background: $green-light;
            padding: 0 8px 0 4px;
            display: flex;
            align-items: center;
            font-size: 12px;

            .file-wrapper-applicant__header__type__icon {
                font-size: 20px;
                color: $green-primary;
            }

            .file-wrapper-applicant__header__type__name {
                color: $green-primary;
                margin-left: 4px;
            }
        }

        .file-wrapper-applicant__header__title {
            margin-left: 8px;
            font-weight: 600;
        }
    }

    .file-wrapper-applicant__nodata {
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        .pt_nodata .pt_nodata-title {
            color: $text-hint;
            font-size: 14px;
        }
    }

    .file-wrapper-applicant__main {
        flex: 1;
        overflow: hidden;
        border: 1px solid $border-light;
        border-radius: 4px;
        box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.14), 0px 0px 4px rgba(0, 0, 0, 0.08);
        display: flex;
        .file-wrapper-applicant__main__item {
            flex: 1;
            height: 100%;
            display: flex;
            flex-direction: column;
            &:not(:first-child) {
                border-left: 1px solid $border-light;
            }
        }
        .file-wrapper-applicant__main__bar {
            height: 32px;
            background: $bg-normal;
            border-bottom: 1px solid $border-light;
            flex-shrink: 0;
            padding-left: 16px;
            display: flex;
            align-items: center;
            justify-content: space-between;

            .pt-ui-btn--diasbled {
                cursor: not-allowed;
                color: $text-disable;
                background-color: transparent;
                border: solid 1px transparent;

                .patsnap-biz-icon {
                    color: $text-disable;
                }
            }
        }
        .file-wrapper-applicant__main__content {
            flex: 1;
            overflow: hidden;
        }

        .file-wrapper-applicant__main__modify {
            .file-wrapper-applicant__main__content {
                overflow-y: auto;
            }
        }

    }
}
</style>
